'use client';

import { alpha, Stack, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import { useMemo } from "react";
import { DateTime } from "luxon";
import getSymbolFromCurrency from 'currency-symbol-map';
import { Event, ComponentGeneralPrice } from "@/app/lib/generated/graphql-types";
import useIsDesktop from "@/hooks/useDesktop";
import { notNull } from "@/app/lib/common/not-null";
import { LocationOn, Link } from "@mui/icons-material";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import CopyButton from "@/app/features/shared/__to_be_integrated/ui/buttons/CopyButton";

const venueLocation = (event: Event): string => {
    let location = '';

    if (event.venue) {
        if (event.venue.title) {
            location += event.venue.title;
        }
        if (event.venue.address?.street) {
            location += `, ${event.venue.address.street}`;
        }
        if (event.venue.address?.zipCode && event.venue.address?.city) {
            location += `, ${event.venue.address.zipCode} ${event.venue.address.city}`;
        }
        if (event.venue.address?.state) {
            location += `, ${event.venue.address.state}`;
        }
        if (event.venue.address?.country) {
            location += `, ${event.venue.address.country}`;
        }
    }

    return location;
}
export default function EventListItem({ event }: { event: Event }) {
    const isDesktop = useIsDesktop();

    return isDesktop ? <DesktopVariant event={event} /> : <MobileVariant event={event} />;
}

function DesktopVariant({ event }: { event: Event }) {
    return (
        <Grid
            container
            spacing={4}
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                pb: 8
            }}
        >
            <Grid size={2}>
                <Stack spacing={4}>
                    <DateView event={event} />
                    <AdditionalInfoView value={event.additionalInfo ?? ''} />
                </Stack>
            </Grid>
            <Grid size={6}>
                <Stack spacing={4}>
                    <DetailsView event={event} />
                    {event.venue && (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <LocationOn fontSize="small" />
                            <Typography variant="subtitle2" fontSize="small">{venueLocation(event)}</Typography>
                        </Stack>
                    )}
                    <PriceList event={event} />
                </Stack>
            </Grid>
            <Grid size={4} sx={{ display: 'flex', flex: '1 1 auto' }}>
                {/*{event.images && (*/}
                {/*    <EventImages images={event.images.filter(notNull)} />*/}
                {/*)}*/}
                {event.image && (
                    <MediaView
                        src={event.image}
                        slots={{
                            root: {
                            },
                            image: {
                                style: {
                                    objectFit: 'contain',
                                    objectPosition: 'top',
                                }
                            }
                        }}
                        magnifier='modal'
                        modalViewType="click"
                        infoViewType="both"
                        showDetailsMenu
                        fill
                    />
                )}
            </Grid>
        </Grid>
    )
}

function MobileVariant({ event }: { event: Event }) {
    return (
        <Stack spacing={4} sx={{ pb: 8 }}>
            <DateView event={event} />
            <Stack spacing={1}>
                <Typography variant="h4">{event.title}</Typography>
                <Typography variant="subtitle2" color="text.secondary">{event.subtitle}</Typography>
            </Stack>
            {event.image && (
                <MediaView
                    src={event.image}
                    width={800}
                    height={1200}
                />
            )}
            <Markdown value={event.description ?? ''} />
            <Typography variant="subtitle2">{venueLocation(event)}</Typography>
            <PriceList event={event} />
        </Stack>
    )
}

function DateView({ event }: { event: Event }) {
    const isDesktop = useIsDesktop();

    const startDate = DateTime.fromISO(event.startsAt);
    const endDate = DateTime.fromISO(event.endsAt);

    const isSameDay = useMemo(() => {
        const startDateTime = DateTime.fromISO(event.startsAt);
        const endDateTime = DateTime.fromISO(event.endsAt);

        return startDateTime.hasSame(endDateTime, 'day');
    }, [event.startsAt, event.endsAt])

    if (isDesktop) {
        return (
            <Stack spacing={0} alignItems="center">
                <Typography variant="body1"><small>{startDate.toFormat('EEE')}</small></Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 0 }} color="primary">{startDate.toFormat('dd')}</Typography>
                <Typography variant="body2" color="primary">{startDate.toFormat('MMM')}</Typography>
            </Stack>
        )
    } else {
        return (
            <Stack>
                {isSameDay
                    ? (
                        <Grid container columns={2} spacing={2} alignItems="baseline">
                            <Grid size={1}>
                                <Typography variant="h4" color="text.secondary">{startDate.toFormat('d. MMM')}</Typography>
                            </Grid>
                            <Grid size={1} textAlign="right">
                                <Typography variant="body1" >{startDate.toFormat('HH:mm')} - {endDate.toFormat('HH:mm')}</Typography>
                            </Grid>
                        </Grid>
                    ): (
                        <>
                            <Grid container columns={2} spacing={2} alignItems="baseline">
                                <Grid size={1}>
                                    <Typography variant="body2" color="text.secondary">{startDate.toFormat('d. MMM')}</Typography>
                                </Grid>
                                <Grid size={1} textAlign="right">
                                    <Typography variant="body1">{startDate.toFormat('HH:mm')}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container columns={2} spacing={2} alignItems="baseline">
                                <Grid size={1}>
                                    <Typography variant="body2" color="text.secondary">{endDate.toFormat('d. MMM')}</Typography>
                                </Grid>
                                <Grid size={1} textAlign="right">
                                    <Typography variant="body1">{endDate.toFormat('HH:mm')}</Typography>
                                </Grid>
                            </Grid>
                        </>
                    )
                }
            </Stack>
        )
    }
}

function AdditionalInfoView({ value }: { value: string | undefined }) {
    if (!value) return null;

    return (
        <Stack spacing={1} alignItems="center">
            <Typography variant="subtitle1" textAlign="center">Info</Typography>
            <Markdown
                value={value}
                typographyOverrides={{ paragraph: "subtitle2" }}
                slots={{
                    root: {
                        sx: {
                            textAlign: 'center'
                        }
                    }
                }}
            />
        </Stack>
    )
}

function PriceList({ event }: { event: Event }) {
    return (
        <Stack spacing={1}>
            {event.prices?.filter(notNull).map((price, index) => (
                <PriceView key={index} price={price} />
            ))}
        </Stack>
    )
}

function PriceView({ price }: { price: ComponentGeneralPrice }) {

    const priceText = useMemo(() => {
        if (price.value === 0) {
            return 'Kostenlos';
        } else  {
            return `${price.value} ${getSymbolFromCurrency(price.currency)}`;
        }
    }, [price]);

    return (
        <Grid
            container
            rowSpacing={0}
            sx={{
                backgroundColor: (theme) => alpha(theme.palette.background.dark, .5),
                padding: (theme) => theme.spacing(1, 2),
            }}
        >
            <Grid size={6} alignContent="center">
                <Typography variant="body1" textAlign="left"  color="text.secondary">
                    {price.purpose}
                </Typography>
            </Grid>
            <Grid size={6} alignContent="center">
                <Typography variant="h4" textAlign="right" color="text.secondary">
                    {priceText}
                </Typography>
            </Grid>
            {price.description && (
                <Grid size={12}>
                    <Typography variant="caption" textAlign="justify" color="textSecondary">
                        {price.description}
                    </Typography>
                </Grid>
            )}
        </Grid>
    )
}

function DetailsView({ event }: { event: Event }) {

    const dateText = useMemo(() => {
        const startDateTime = DateTime.fromISO(event.startsAt);
        const endDateTime = DateTime.fromISO(event.endsAt);

        if (startDateTime.hasSame(endDateTime, 'day')) {
            const start = startDateTime.toFormat('d. MMMM yy | HH:mm');
            const end = endDateTime.toFormat('HH:mm');

            return `${start} - ${end}`;
        } else {
            const start = startDateTime.toFormat('d. MMMM yy | HH:mm');
            const end = endDateTime.toFormat('d. MMMM yy | HH:mm');

            return `${start} - ${end}`;
        }
    }, [event.startsAt, event.endsAt]);

    return (
        <Stack alignItems="flex-start">
            <Stack spacing={1} sx={{ paddingBottom: 4, width: '100%' }}>
                <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" color="textPrimary">{dateText}</Typography>
                    <Tooltip title="Link kopieren">
                        <CopyButton content={`${window.location.origin}/events?id=${event.documentId}`}><Link /></CopyButton>
                    </Tooltip>
                </Stack>
                <Typography variant="h2" color="primary">{event.title}</Typography>
                <Typography variant="subtitle2" fontSize="small">{event.subtitle}</Typography>
            </Stack>
            <Markdown value={event.description ?? ''} slots={{ root: { color: 'text.primary' }}} />
        </Stack>
    );
}
